import { isCancel } from 'axios';
import loginRedirect from '@utils/login-redirect';

const hasOwnProperties = (obj, ...props) => props.filter(prop => obj.hasOwnProperty(prop)).length === props.length;

/** @typedef {{ success: boolean, data: (?Object<string, any>|?Array<any>), error: ?string }} ResponseClass */

class Response {
    /** @type {ResponseClass} config */
    constructor({ cancelled, data, error, status, success }) {
        if(typeof success === 'undefined' || typeof status === 'undefined') {
            throw new Error('All Response instances must have a success state and status code')
        }

        this.cancelled = cancelled ?? false;
        this.data = data ?? null;
        this.error = error ?? null;
        this.status = status;
        this.success = success;
    }
}

function httpSuccess(response) {
    const status = response.status;
    let data = response.data;
    let error = null;
    let success = true;

    // if these three properties are in the response body, then the API is using the standard response
    if(hasOwnProperties(response.data, 'Data', 'ErrorMessage', 'Success')) {
        // if this is the case, then we can flatten the response
        data = response.data.Data;
        error = response.data.ErrorMessage;
        success = response.data.Success;

        if(error === '401') {
            loginRedirect(response.data.RedirectUrl)
            return;
        };
    }
    
    return new Response({
        data,
        error,
        status,
        success
    });
}

/**
 * handle http error messaging for response
 * - will always be an error
 * @param {Object<string, any>} err
 * @return {ResponseClass} formatted response data
 */
function httpError(err) {
    let error = err.message;
    let status = null;

    if(err.response) {
        const msg = err.response.data?.errors || 'Server Error:';
        error = `${msg}: ${err.response.status}`;
        status = err.response.status;
    } else if(err.request) {
        error = 'Request was made, but there was no response from server.';
    } else {
        const errObj = err.toJSON();
        status = errObj.status;
    }

    return new Response({
        cancelled: isCancel(err),
        error,
        status,
        success: false
    });
}

/**
 * send http request and format the response
 * @param {Function} callback promise or async function used to send http request
 * @return {ResponseClass} formatted response data
 */
export default async function(callback) {
    try {
        const response = await callback();
        return httpSuccess(response);
    } catch (err) {
        return httpError(err);
    }
}
